import { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Sparkles, Send, Loader2, Check, Pencil, Calendar, Zap, AlertTriangle,
  MessageSquarePlus, History, ChevronRight, Flame,
} from "lucide-react";
import api from "../lib/apiClient";
import { useAuth } from "../context/AuthContext";
import { PROJECT_TYPE_META } from "../lib/gameData";
import { toast } from "sonner";

const PROMPT_PILLS = [
  "Je veux lancer un nouveau projet",
  "Je veux développer mon audience",
  "Je veux créer du contenu régulier",
  "Je veux préparer un lancement",
  "Je veux structurer mon activité",
];

const PRIORITY_STYLE = {
  Haute: { dot: "🔴", cls: "text-red-400 border-red-500/40 bg-red-500/10" },
  Moyenne: { dot: "🟠", cls: "text-amber-400 border-amber-500/40 bg-amber-500/10" },
  Faible: { dot: "🟢", cls: "text-emerald-400 border-emerald-500/40 bg-emerald-500/10" },
};

export default function Assistant() {
  const { refresh } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [messages, setMessages] = useState([]);
  const [convId, setConvId] = useState(null);
  const [plan, setPlan] = useState(null);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [creating, setCreating] = useState(false);
  const [modifyMode, setModifyMode] = useState(false);
  const [conversations, setConversations] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [overdue, setOverdue] = useState(null);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  const loadConversations = useCallback(async () => {
    const { data } = await api.get("/assistant/conversations");
    setConversations(data);
  }, []);

  useEffect(() => {
    (async () => {
      const ov = await api.get("/assistant/overdue");
      if (ov.data.count > 0) setOverdue(ov.data);
      const { data } = await api.get("/assistant/conversations");
      setConversations(data);
      const prefill = location.state?.prefill;
      if (prefill) {
        navigate(location.pathname, { replace: true, state: {} });
        send(prefill, true);
        return;
      }
      if (data.length > 0) {
        try {
          const { data: conv } = await api.get(`/assistant/conversations/${data[0].id}`);
          setConvId(conv.id);
          setMessages(conv.messages.map((m) => ({ role: m.role, content: m.content })));
          setPlan(conv.last_plan || null);
        } catch {}
      }
    })();
  }, []);

  useEffect(() => { scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" }); }, [messages, plan]);

  const send = async (text, forceNew = false) => {
    const t = (text ?? input).trim();
    if (!t || sending) return;
    if (forceNew) { setMessages([]); setPlan(null); }
    setMessages((m) => [...m, { role: "user", content: t }]);
    setInput("");
    setSending(true);
    try {
      const { data } = await api.post("/assistant/message", { message: t, conversation_id: forceNew ? null : convId });
      setConvId(data.conversation_id);
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
      setPlan(data.plan || null);
      setModifyMode(false);
      loadConversations();
    } catch {
      toast.error("L'assistant n'a pas pu répondre. Réessaie.");
    } finally {
      setSending(false);
    }
  };

  const createProject = async () => {
    setCreating(true);
    try {
      const { data } = await api.post("/assistant/confirm", { conversation_id: convId });
      toast.success(`Projet « ${data.project.title} » créé avec ${data.tasks_created} tâches !`);
      (data.new_badges || []).forEach(() => toast.success("🏅 Nouveau badge débloqué !"));
      setPlan(null);
      setMessages((m) => [...m, { role: "assistant", content: "🎉 C'est fait ! Ton projet et tes tâches sont dans « Mes projets » et « Tâches ». On avance !" }]);
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Impossible de créer le projet.");
    } finally {
      setCreating(false);
    }
  };

  const startModify = () => {
    setModifyMode(true);
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  const openConversation = async (id) => {
    const { data } = await api.get(`/assistant/conversations/${id}`);
    setConvId(data.id);
    setMessages(data.messages.map((m) => ({ role: m.role, content: m.content })));
    setPlan(data.last_plan || null);
    setShowHistory(false);
  };

  const newConversation = () => {
    setConvId(null); setMessages([]); setPlan(null); setModifyMode(false); setShowHistory(false);
  };

  const reorganize = async () => {
    const { data } = await api.post("/assistant/reorganize");
    toast.success(data.message);
    setOverdue(null);
  };

  return (
    <div className="flex flex-col h-[calc(100vh-8rem)] md:h-[calc(100vh-6rem)]">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl gold-gradient flex items-center justify-center glow-gold shrink-0">
            <Sparkles className="w-6 h-6 text-black" />
          </div>
          <div>
            <h1 className="font-display text-2xl font-extrabold">Assistant Cehnura</h1>
            <p className="text-zinc-500 text-xs sm:text-sm">Dis-moi ce que tu veux accomplir. Je transforme ton objectif en plan d'action.</p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button onClick={() => { setShowHistory((v) => !v); loadConversations(); }} data-testid="assistant-history-btn"
            className="w-10 h-10 rounded-xl border border-zinc-800 text-zinc-400 hover:text-amber-400 hover:border-amber-500/40 flex items-center justify-center"><History className="w-5 h-5" /></button>
          <button onClick={newConversation} data-testid="assistant-new-btn"
            className="w-10 h-10 rounded-xl border border-zinc-800 text-zinc-400 hover:text-amber-400 hover:border-amber-500/40 flex items-center justify-center"><MessageSquarePlus className="w-5 h-5" /></button>
        </div>
      </div>

      {/* History drawer */}
      <AnimatePresence>
        {showHistory && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}
            className="mb-4 bg-[#18181E] border border-zinc-800 rounded-2xl overflow-hidden" data-testid="assistant-conversations">
            {conversations.length === 0 ? <p className="p-4 text-sm text-zinc-500">Aucune conversation.</p> :
              conversations.map((c) => (
                <button key={c.id} onClick={() => openConversation(c.id)} data-testid={`conversation-${c.id}`}
                  className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-zinc-800/60 border-b border-zinc-800/60 last:border-0">
                  <span className="text-sm truncate">{c.title}</span>
                  <ChevronRight className="w-4 h-4 text-zinc-600 shrink-0" />
                </button>
              ))}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Overdue banner */}
      {overdue && (
        <div className="mb-4 bg-red-500/10 border border-red-500/30 rounded-2xl p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="overdue-banner">
          <AlertTriangle className="w-5 h-5 text-red-400 shrink-0" />
          <p className="text-sm text-red-200 flex-1">{overdue.message}</p>
          <div className="flex gap-2">
            <button onClick={reorganize} data-testid="reorganize-btn" className="px-3 h-9 rounded-lg bg-red-500/20 text-red-300 border border-red-500/40 text-sm font-medium hover:bg-red-500/30">Réorganiser</button>
            <button onClick={() => setOverdue(null)} data-testid="keep-plan-btn" className="px-3 h-9 rounded-lg border border-zinc-700 text-zinc-400 text-sm hover:border-zinc-500">Garder</button>
          </div>
        </div>
      )}

      {/* Chat area */}
      <div className="flex-1 bg-[#18181E] border border-zinc-800 rounded-3xl flex flex-col min-h-0">
        <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 sm:p-5 space-y-4" data-testid="assistant-chat">
          {messages.length === 0 && (
            <div className="text-center py-8">
              <p className="text-zinc-400 text-sm mb-4">Choisis un objectif ou écris le tien 👇</p>
              <div className="flex flex-wrap justify-center gap-2">
                {PROMPT_PILLS.map((p) => (
                  <button key={p} onClick={() => send(p)} data-testid={`pill-${p.slice(0, 12)}`}
                    className="text-xs px-3.5 h-9 rounded-full bg-zinc-900 border border-zinc-800 text-zinc-300 hover:border-amber-500/50 hover:text-amber-400 transition-colors">
                    {p}
                  </button>
                ))}
              </div>
            </div>
          )}
          {messages.map((m, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
              className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm ${m.role === "user" ? "gold-gradient text-black font-medium" : "bg-zinc-900 text-zinc-200 border border-zinc-800"}`}>
                {m.content}
              </div>
            </motion.div>
          ))}

          {sending && (
            <div className="flex justify-start">
              <div className="bg-zinc-900 border border-zinc-800 rounded-2xl px-4 py-3">
                <Loader2 className="w-4 h-4 text-amber-400 animate-spin" />
              </div>
            </div>
          )}

          {/* Plan preview */}
          {plan && !sending && (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
              className="bg-zinc-900 border border-amber-500/30 rounded-2xl overflow-hidden" data-testid="plan-preview">
              <div className="p-4 border-b border-zinc-800 flex items-center gap-3">
                <span className="text-2xl">{PROJECT_TYPE_META[plan.type] || "✨"}</span>
                <div className="flex-1">
                  <p className="text-xs font-mono uppercase tracking-wider text-amber-500">Projet proposé</p>
                  <h3 className="font-display text-lg font-bold" data-testid="plan-title">{plan.title}</h3>
                </div>
                <div className="text-right">
                  <p className="text-xs text-zinc-500 flex items-center gap-1 justify-end"><Calendar className="w-3 h-3" /> {plan.duration_days} jours</p>
                  <p className="text-xs text-zinc-500">0 %</p>
                </div>
              </div>
              <div className="p-4 space-y-2 max-h-72 overflow-y-auto">
                <p className="text-xs font-mono uppercase tracking-wider text-zinc-500 mb-1">Plan proposé — {plan.tasks.length} tâches</p>
                {plan.tasks.map((t, idx) => {
                  const ps = PRIORITY_STYLE[t.priority] || PRIORITY_STYLE.Moyenne;
                  return (
                    <div key={idx} className="flex items-start gap-3 bg-[#0F0F13] rounded-xl p-3" data-testid={`plan-task-${idx}`}>
                      <span className="font-mono text-xs text-amber-400 w-10 shrink-0 pt-0.5">{t.day_label}</span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium">{t.title}</p>
                        {t.description && <p className="text-xs text-zinc-500 mt-0.5">{t.description}</p>}
                        <div className="flex items-center gap-2 mt-1.5">
                          <span className={`text-[10px] px-1.5 py-0.5 rounded-full border ${ps.cls}`}>{ps.dot} {t.priority}</span>
                          <span className="font-mono text-xs text-amber-400">+{t.xp} XP</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="p-4 border-t border-zinc-800 flex gap-2">
                <button onClick={createProject} disabled={creating} data-testid="plan-create-btn"
                  className="flex-1 h-11 rounded-xl gold-gradient text-black font-semibold flex items-center justify-center gap-2 hover:opacity-90 disabled:opacity-60">
                  {creating ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Check className="w-5 h-5" /> Créer le projet</>}
                </button>
                <button onClick={startModify} data-testid="plan-modify-btn"
                  className="px-4 h-11 rounded-xl border border-zinc-700 text-zinc-300 font-medium flex items-center gap-2 hover:border-amber-500/50">
                  <Pencil className="w-4 h-4" /> Modifier
                </button>
              </div>
            </motion.div>
          )}
        </div>

        {/* Input */}
        <div className="p-3 border-t border-zinc-800">
          {modifyMode && <p className="text-xs text-amber-400 mb-2 px-1">✏️ Dis-moi ce que tu veux changer (ex : « je sors dans 10 jours » ou « je n'ai pas encore la cover »)</p>}
          <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex gap-2">
            <input ref={inputRef} value={input} onChange={(e) => setInput(e.target.value)} data-testid="assistant-input"
              placeholder="Exemple : Je veux lancer mon projet dans 3 semaines."
              className="flex-1 h-12 px-4 rounded-xl bg-zinc-900 border border-zinc-800 text-zinc-100 placeholder-zinc-600 focus:border-amber-500 outline-none" />
            <button type="submit" disabled={sending} data-testid="assistant-send"
              className="w-12 h-12 rounded-xl gold-gradient text-black flex items-center justify-center hover:opacity-90 shrink-0 disabled:opacity-60">
              <Send className="w-5 h-5" />
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
